<template>
  <div class="space-y-6">
    <div class="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
      <!-- 标题和状态筛选 -->
      <div class="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <h2 class="text-lg font-bold text-neutral-900">上传管理</h2>
        <div class="flex items-center gap-4">
          <!-- 状态筛选 Tab -->
          <div class="bg-neutral-50 p-1 rounded-lg border border-neutral-200 flex text-sm font-medium">
            <button
              v-for="tab in tabs"
              :key="tab.value"
              @click="changeStatus(tab.value)"
              :class="[
                'px-4 py-2 rounded-md transition-all',
                currentStatus === tab.value
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-neutral-600 hover:bg-white',
              ]"
            >
              {{ tab.label }}
            </button>
          </div>
          <div class="flex items-center space-x-2 bg-neutral-50 px-3 py-2 rounded-lg border border-neutral-200">
            <span class="text-sm text-neutral-500">
              共 <span class="font-bold text-neutral-900">{{ total }}</span> 条
            </span>
            <button
              @click="$emit('refresh')"
              class="text-neutral-400 hover:text-primary transition-colors ml-2"
              title="刷新列表"
            >
              <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- 上传列表（两列并排） -->
      <div v-loading="loading" class="space-y-4">
        <div v-if="list.length > 0" class="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div
            v-for="tool in list"
            :key="tool.id"
            class="bg-neutral-50 rounded-xl border border-neutral-200 p-4 sm:p-6 hover:shadow-md transition-shadow duration-200"
          >
            <div class="flex flex-col sm:flex-row gap-6">
              <!-- 工具封面 -->
              <div class="flex-shrink-0 w-full sm:w-32 h-32 sm:h-32">
                <img
                  :src="tool.cover"
                  :alt="tool.title"
                  class="w-full h-full object-cover rounded-lg border border-neutral-100 cursor-pointer"
                  @click="$emit('view-detail', tool)"
                />
              </div>

              <!-- 工具信息 -->
              <div class="flex-1 min-w-0 flex flex-col justify-between">
                <div>
                  <div class="flex justify-between items-start">
                    <h3
                      class="text-lg font-bold text-neutral-900 mb-1 cursor-pointer hover:text-primary transition-colors"
                      @click="$emit('view-detail', tool)"
                    >
                      {{ tool.title }}
                    </h3>
                    <span
                      class="text-xs px-2 py-1 rounded-full"
                      :class="{
                        'bg-yellow-100 text-yellow-700': tool.status === 0,
                        'bg-green-100 text-green-700': tool.status === 1,
                        'bg-red-100 text-red-700': tool.status === 2,
                      }"
                    >
                      {{ getStatusText(tool.status) }}
                    </span>
                  </div>
                  <div class="flex items-center text-sm text-neutral-500 mb-2">
                    <span class="mr-4">
                      <i class="fa fa-folder-o mr-1"></i>{{ getCategoryName(tool.categoryid) }}
                    </span>
                    <span>
                      <i class="fa fa-clock-o mr-1"></i>{{ formatDate(tool.createTime) }}
                    </span>
                  </div>
                  <p class="text-neutral-600 text-sm line-clamp-2 mb-3">
                    {{ tool.description }}
                  </p>
                </div>

                <!-- 附件信息 -->
                <div
                  v-if="tool.fileId"
                  class="flex items-center text-sm text-neutral-500 bg-white px-3 py-2 rounded border border-neutral-200 w-fit hover:text-primary cursor-pointer"
                  @click="$emit('download', tool)"
                >
                  <i class="fa fa-file-archive-o mr-2 text-primary"></i>
                  <span>下载附件</span>
                </div>
              </div>

              <!-- 操作按钮 -->
              <div class="flex sm:flex-col justify-end sm:justify-center gap-3 border-t sm:border-t-0 sm:border-l border-neutral-200 pt-4 sm:pt-0 sm:pl-6">
                <!-- 待审批状态：显示通过和拒绝 -->
                <template v-if="tool.status === 0">
                  <button
                    @click="$emit('approve', tool)"
                    class="flex-1 sm:flex-none px-4 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium flex items-center justify-center"
                  >
                    <i class="fa fa-check mr-2"></i>通过
                  </button>
                  <button
                    @click="$emit('reject', tool)"
                    class="flex-1 sm:flex-none px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium flex items-center justify-center"
                  >
                    <i class="fa fa-times mr-2"></i>拒绝
                  </button>
                </template>

                <!-- 已上架状态：显示下架 -->
                <template v-if="tool.status === 1">
                  <button
                    @click="$emit('reject', tool)"
                    class="flex-1 sm:flex-none px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium flex items-center justify-center"
                  >
                    <i class="fa fa-arrow-down mr-2"></i>下架
                  </button>
                </template>

                <!-- 已拒绝状态：显示重新上架 -->
                <template v-if="tool.status === 2">
                  <button
                    @click="$emit('approve', tool)"
                    class="flex-1 sm:flex-none px-4 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium flex items-center justify-center"
                  >
                    <i class="fa fa-refresh mr-2"></i>重新上架
                  </button>
                </template>

                <button
                  @click="$emit('delete', tool)"
                  class="flex-1 sm:flex-none px-4 py-2 bg-neutral-50 text-neutral-600 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors text-sm font-medium flex items-center justify-center"
                >
                  <i class="fa fa-trash-o mr-2"></i>删除
                </button>
              </div>
            </div>
          </div>

          <!-- 分页 -->
          <div class="flex justify-center mt-8">
            <el-pagination
              background
              layout="prev, pager, next"
              :total="total"
              :page-size="pageSize"
              :current-page="page"
              @current-change="$emit('page-change', $event)"
            />
          </div>
        </div>

        <!-- 空状态 -->
        <div
          v-else
          class="bg-white rounded-xl border border-neutral-200 p-16 text-center"
        >
          <div class="w-20 h-20 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-6 text-neutral-400">
            <i class="fa fa-check-circle-o text-3xl"></i>
          </div>
          <h3 class="text-lg font-medium text-neutral-900 mb-2">
            暂无{{ getStatusText(currentStatus) }}任务
          </h3>
          <p class="text-neutral-500 text-sm">当前列表为空</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "UploadManager",
  props: {
    list: {
      type: Array,
      required: true,
    },
    total: {
      type: Number,
      default: 0,
    },
    page: {
      type: Number,
      default: 1,
    },
    pageSize: {
      type: Number,
      default: 10,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    currentStatus: {
      type: Number,
      default: 0,
    },
    categoryList: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["approve", "reject", "delete", "view-detail", "download", "refresh", "page-change", "status-change"],
  data() {
    return {
      tabs: [
        { label: "待审批", value: 0 },
        { label: "已上架", value: 1 },
        { label: "已拒绝", value: 2 },
      ],
    };
  },
  methods: {
    changeStatus(status) {
      this.$emit("status-change", status);
    },
    getStatusText(status) {
      const map = { 0: "待审批", 1: "已上架", 2: "已拒绝" };
      return map[status] || "未知";
    },
    getCategoryName(id) {
      const category = this.categoryList.find((c) => c.id === id);
      return category ? category.title : "未知分类";
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      const h = String(date.getHours()).padStart(2, "0");
      const min = String(date.getMinutes()).padStart(2, "0");
      return `${y}-${m}-${d} ${h}:${min}`;
    },
  },
};
</script>
